/**
 * Hook personalizado para manejar la exportación de submissions a CSV
 * Abstrae toda la lógica de negocio relacionada con exportación de datos
 */

import { useState } from 'react';
import { toast } from 'sonner';

interface ExportOptions {
  formId: string;
  formTitle?: string;
  limit?: number;
  offset?: number;
}

interface UseSubmissionExportReturn {
  exportToCSV: (options: ExportOptions) => Promise<void>;
  isExporting: boolean;
  error: Error | null;
}

export function useSubmissionExport(): UseSubmissionExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const exportToCSV = async (options: ExportOptions) => {
    const { formId, formTitle, limit = 1000, offset = 0 } = options;

    try {
      setIsExporting(true);
      setError(null);

      // Validación
      if (!formId) {
        throw new Error('El ID del formulario es requerido para exportar');
      }

      // Obtener credenciales de Supabase
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Configuración de Supabase no encontrada');
      }

      console.log('📤 [Export] Iniciando exportación para formulario:', formId);
      toast.info('Descargando submissions desde JotForm...', { id: 'export-csv' });

      // Llamar a la Edge Function con exportCsv=true
      const response = await fetch(`${supabaseUrl}/functions/v1/jotform-api`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          action: 'get-submissions',
          params: {
            formId,
            limit,
            offset,
            exportCsv: true  // Importante: esto activa la lógica de exportación en la Edge Function
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('❌ [Export] Error en respuesta:', errorData);
        throw new Error(`Error al exportar: ${response.status} ${response.statusText}`);
      }

      // La Edge Function devuelve CSV directamente (text/csv)
      const blob = await response.blob();

      console.log('✅ [Export] CSV recibido, tamaño:', blob.size, 'bytes');

      // Generar nombre de archivo sanitizado
      const sanitizedFormName = formTitle
        ? formTitle.replace(/[^a-zA-Z0-9-_]/g, '_')
        : 'formulario';

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `jotform_${sanitizedFormName}_${timestamp}.csv`;

      // Crear URL temporal y descargar
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;

      // Añadir al DOM, hacer click, y limpiar
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('✅ [Export] Archivo descargado:', filename);
      toast.success('Descarga completada', { id: 'export-csv' });

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      console.error('❌ [Export] Error exportando:', err);

      setError(err instanceof Error ? err : new Error(errorMessage));
      toast.error(`Error al exportar: ${errorMessage}`, { id: 'export-csv' });

      throw err;
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportToCSV,
    isExporting,
    error
  };
}
